// Added based current swagger as of 8.18.2023

import { isTestEnv } from "constants/environment";
import { GetProductLinesQueryParameters } from "types/api/globals/getProductLines.ts";
import { GetEmulationAccountsQueryParameters } from "types/api/accounts/getEmulationAccounts.ts";

export const baseURL = isTestEnv ? new URL("", window.location.origin).href : `${import.meta.env.VITE_APP_BASEURL}`;

const endpoints = {
	addresses: {
		getSuggestions: (address = ":address") => {
			return `${baseURL}/api/v1/addresses/suggestions/${address}`;
		},
		validateAddress: () => {
			return `${baseURL}/api/v1/addresses/validate`;
		}
	},
	accounts: {
		getAccounts: (productLineCode?: string | null) => {
			const params = new URLSearchParams();
			if (productLineCode) params.append("productLineCode", productLineCode);
			const queryString = params.size > 0 ? `?${params.toString()}` : "";
			return `${baseURL}/api/v1/accounts${queryString}`;
		},
		getBillToShipTos: (accountId: string, billToId: string) => {
			return `${baseURL}/api/v1/accounts/${accountId}/bill-tos/${billToId}/ship-tos`;
		},
		getBillToInfo: (accountId: string = ":accountId", billToId: string = ":billToId") => {
			return `${baseURL}/api/v1/accounts/${accountId}/bill-tos/${billToId}`;
		},
		getAccountsByOrderId: (orderId: string) => {
			return `${baseURL}/api/v1/accounts?orderId=${orderId}`;
		},
		getEmulationAccounts: (queryParams?: GetEmulationAccountsQueryParameters) => {
			const queryString = queryParams ? `?accountNumber=${queryParams.accountNumber}` : "";
			return `${baseURL}/api/v1/accounts/emulation${queryString}`;
		},
		getTrucksAccounts: () => {
			return `${baseURL}/api/v1/accounts/trucks`;
		},
		getMerchandiseAccounts: () => {
			return `${baseURL}/api/v1/accounts/merchandise`;
		},
		getMerchandiseShipTos: (accountId: string = ":accountId") => {
			return `${baseURL}/api/v1/accounts/merchandise/${accountId}/ship-tos`;
		},
		addNewMerchandiseShipTo: () => {
			return `${baseURL}/api/v1/accounts/merchandise/ship-tos`;
		}
	},
	designers: {
		getDesigners: (accountId?: string) => {
			return `${baseURL}/api/v1/accounts/${accountId || ":accountId"}/designers`;
		}
	},
	globals: {
		getProductLines: (params?: GetProductLinesQueryParameters) => {
			const queryString =
				params && params?.accountId && params?.billToId
					? `?accountId=${params.accountId}&billToId=${params?.billToId}`
					: "";
			return `${baseURL}/api/v1/globals/product-lines${queryString}`;
		},
		getProductLine: (productLineId = ":productLineId") => {
			return `${baseURL}/api/v1/globals/product-lines/${productLineId}`;
		},
		getPaintVendors: (productLineId?: string) => {
			return `${baseURL}/api/v1/globals/product-lines/${productLineId}/vendors`;
		},
		getStyle: (productLineId = ":productLineId", styleId = ":styleId") => {
			return `${baseURL}/api/v1/globals/product-lines/${productLineId}/styles/${styleId}`;
		},
		getSpecies: (
			productLineId = ":productLineId",
			styleId = ":styleId",
			speciesId = ":speciesId",
			customFinishes?: boolean
		) => {
			const queryString = customFinishes !== undefined ? `?customFinishes=${customFinishes}` : "";
			return `${baseURL}/api/v1/globals/product-lines/${productLineId}/styles/${styleId}/species/${speciesId}${queryString}`;
		},
		getFinish: (
			productLineId = ":productLineId",
			styleId = ":styleId",
			speciesId = ":speciesId",
			finishId = ":finishId"
		) => {
			return `${baseURL}/api/v1/globals/product-lines/${productLineId}/styles/${styleId}/species/${speciesId}/finishes/${finishId}`;
		}
	},
	images: {
		getUploadDataById: (tandemOrderId: string) => {
			return `${baseURL}/api/v1/images/${tandemOrderId}/upload`;
		},
		getDownloadDataById: (tandemOrderId: string) => {
			return `${baseURL}/api/v1/images/${tandemOrderId}/download`;
		},
		deleteDownloadDataById: (tandemOrderId: string) => {
			return `${baseURL}/api/v1/images/${tandemOrderId}/delete`;
		}
	},
	orders: {
		getOrders: () => {
			return `${baseURL}/api/v2/orders`;
		},
		getOrdersById: (orderId: string = ":id") => {
			return `${baseURL}/api/v3/orders/${orderId}`;
		},
		postReplacementOrder: () => {
			return `${baseURL}/api/v1/orders/replacements`;
		},
		getLinkedOrders: (orderId: string = ":id") => {
			return `${baseURL}/api/v1/orders/${orderId}/linked-orders`;
		},
		draftOrders: () => {
			return `${baseURL}/api/v1/orders/drafts`;
		},
		submitDraftOrder: () => {
			return `${baseURL}/api/v1/orders/drafts/submit`;
		},
		exportDraftById: (draftId: string = ":id") => {
			return `${baseURL}/api/v1/orders/drafts/${draftId}/export`;
		},
		getTemplates: () => {
			return `${baseURL}/api/v1/orders/drafts?type=Template`;
		},
		getQuotes: () => {
			return `${baseURL}/api/v1/orders/drafts?type=Quote`;
		},
		draftById: (draftId: string = ":id") => {
			return `${baseURL}/api/v1/orders/drafts/${draftId}`;
		},
		templateById: (templateId: string = ":id") => {
			return `${baseURL}/api/v1/orders/drafts/${templateId}?type=Template`;
		}
	},
	users: {
		getUserDetails: () => {
			return `${baseURL}/api/v1/users`;
		},
		getUserEmulation: () => {
			return `${baseURL}/api/v1/users/emulation`;
		}
	},
	newOrder: {
		parseCatalogs: () => {
			return `${baseURL}/api/v1/orders/parse/catalogs`;
		},
		parseOrder: () => {
			return `${baseURL}/api/v1/orders/parse`;
		},
		postNewOrder: () => `${baseURL}/api/v1/orders`,
		cancelOrder: (orderId: string | null = ":orderId") => `${baseURL}/api/v1/orders/${orderId}/cancel`,
		validateOrderRequest: () => {
			return `${baseURL}/api/v1/products/validate`;
		},
		getBillToShipTosNewOrders: () => {
			return `${baseURL}/api/v1/accounts/bill-tos/ship-tos`;
		},
		getProductsByProductLineCode: (productLineCode = "", productId?: string, sampleProducts?: boolean) => {
			const queryParams = new URLSearchParams();
			productLineCode && queryParams.append("productLineId", productLineCode);
			productId && queryParams.append("productId", productId);
			sampleProducts && queryParams.append("sampleProducts", String(sampleProducts));
			const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

			return `${baseURL}/api/v1/products${queryString}`;
		},
		getModifications: (productId = ":productId", productLineId?: string, includeCQComplexityMods?: boolean) => {
			const params = new URLSearchParams();
			params.append("productId", productId);
			if (productLineId) params.append("productLineId", productLineId);
			if (includeCQComplexityMods !== undefined)
				params.append("includeCQComplexityMods", String(includeCQComplexityMods));
			const queryString = params.size > 0 ? `?${params.toString()}` : "";
			return `${baseURL}/api/v1/products/modifications${queryString}`;
		},
		getSpecialModifications: () => {
			return `${baseURL}/api/v1/products/special-modifications`;
		}
	},
	configurations: {
		getConfigurations: () => {
			return `${baseURL}/api/v1/configurations`;
		},
		getCqComplexityTierConfig: () => {
			return `${baseURL}/api/v1/configurations/complexity-tier-config`;
		}
	},
	trucks: {
		trucks: (truckId?: string) => {
			return `${baseURL}/api/v1/trucks/${truckId ?? ""}`;
		},
		getTruckDrafts: (truckId: string = ":truckId") => {
			return `${baseURL}/api/v1/trucks/${truckId}/drafts`;
		},
		submitTruck: (truckId: string = ":truckId") => {
			return `${baseURL}/api/v1/trucks/${truckId ?? ""}/submit`;
		}
	},
	customQuotes: {
		customQuotes: (customQuoteId?: string) => {
			return `${baseURL}/api/v1/custom-quotes/${customQuoteId ?? ""}`;
		},
		getCustomQuoteById: (customQuoteId: string = ":customQuoteId") => {
			return `${baseURL}/api/v1/custom-quotes/${customQuoteId}`;
		},
		getQuoters: () => {
			return `${baseURL}/api/v1/users/quoters`;
		},
		comments: (customQuoteId: string = ":customQuoteId") => {
			return `${baseURL}/api/v1/custom-quotes/${customQuoteId}/comments`;
		},
		declineCustomQuote: (customQuoteId: string = ":customQuoteId") => {
			return `${baseURL}/api/v1/custom-quotes/${customQuoteId}/decline`;
		},
		createRevision: (customQuoteId: string = ":customQuoteId") => {
			return `${baseURL}/api/v1/custom-quotes/${customQuoteId}/revisions`;
		},
		revisions: (customQuoteId: string = ":customQuoteId", revisionId: string | number = ":revisionId") => {
			return `${baseURL}/api/v1/custom-quotes/${customQuoteId}/revisions/${revisionId}`;
		},
		getAutocadFile: (customQuoteId: string = ":customQuoteId", revisionId: string | number = ":revisionId") => {
			return `${baseURL}/api/v1/custom-quotes/${customQuoteId}/revisions/${revisionId}/autocad`;
		},
		getComplexityScores: (sku: string = ":sku") => {
			return `${baseURL}/api/v1/custom-quotes/${sku}/complexity-scores`;
		}
	},
	pdf: {
		getAcknowledgementsPdf: (orderId: string | null = ":orderId", layoutFormat: string | null = ":layoutFormat") =>
			`${baseURL}/api/v1/pdfs/acknowledgements/${orderId}/format/${layoutFormat}`
	},
	blob: {
		uploadBlob: (containerId: string, fileName: string) =>
			`https://tandemdatastoragelocal.blob.core.windows.net/${containerId}/${fileName}`
	},
	preferences: {
		userPreferences: () => {
			return `${baseURL}/api/v1/preferences`;
		}
	},
	permissions: {
		getUserPermissions: () => {
			return `${baseURL}/api/v1/permissions`;
		},
		getUserPermissionsById: (userId: string | null = ":userId") => {
			return `${baseURL}/api/v1/permissions/${userId}`;
		},
		getAdminSearchResults: (searchCriteria: string) => {
			return `${baseURL}/api/v1/permissions/admin?searchCriteria=${searchCriteria}`;
		},
		getUsers: () => {
			return `${baseURL}/api/v1/permissions/users`;
		},
		updateUserPermissions: (userId: string | null = ":userId") => {
			return `${baseURL}/api/v1/permissions/users/${userId}`;
		},
		updateBillToEmailRecipients: () => {
			return `${baseURL}/api/v1/permissions/billto`;
		}
	},
	leads: {
		getLeads: () => {
			return `${baseURL}/api/v1/leads/stores/assignments`;
		},
		updateLeadAssignment: (leadId: string, leadAssignmentId: string) => {
			return `${baseURL}/api/v1/leads/${leadId ?? ""}/assignments/${leadAssignmentId ?? ""}`;
		},
		getLeadAssignmentOptions: (leadAssignmentId: string) => {
			return `${baseURL}/api/v1/leads/assignments/${leadAssignmentId ?? ""}/assignment-options`;
		}
	},
	marketingMaterials: {
		getMerchandiseProducts: () => {
			return `${baseURL}/api/v1/products/merchandise`;
		},
		getMerchandiseProductsById: (productId: string | null = ":productId") => {
			return `${baseURL}/api/v1/products/merchandise/${productId}`;
		},
		shoppingCart: () => {
			return `${baseURL}/api/v1/shopping-cart`;
		},
		getMerchandiseFilters: () => {
			return `${baseURL}/api/v1/products/merchandise-filters`;
		},
		shoppingCartTemplates: (templateId: string | null = null) => {
			const templateIdPath = templateId ? `/${templateId}` : "";
			return `${baseURL}/api/v1/shopping-cart/templates${templateIdPath}`;
		},
		validateShoppingCart: (accountId: string | null = ":productId") => {
			const queryParams = new URLSearchParams();
			if (accountId) {
				queryParams.append("accountId", accountId);
			}
			const queryString = queryParams.size ? `?${queryParams.toString()}` : "";
			return `${baseURL}/api/v1/shopping-cart/validate${queryString}`;
		}
	},
	stores: {
		getStores: () => {
			return `${baseURL}/api/v1/stores`;
		},
		getManageableStores: () => {
			return `${baseURL}/api/v1/stores/manageable-stores`;
		},
		getStoreSettings: (storeId: string = ":storeId") => {
			return `${baseURL}/api/v1/stores/${storeId}/settings`;
		},
		updateStoreSettings: (storeId: string = ":storeId") => {
			return `${baseURL}/api/v1/stores/${storeId}/settings`;
		}
	}
};

export default endpoints;
